import {
    format,
    formatWithoutTimezone,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard, KeyboardAvoidingView } from 'react-native';
import {
    BodyMediumRegular,
    ControlledDateTextInput,
    Form,
    H5Emphasized,
    isNonMobileWeb,
    Private,
    USE_FORM_MODE_DEFAULTS,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const dobSchema = z.object({
    dob: zodDateOfBirthValidation,
});

type DobSchema = z.infer<typeof dobSchema>;

interface DobProps {
    dateOfBirth?: string | null;
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
}

const Dob = ({ dateOfBirth, onPressNext }: DobProps) => {
    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<DobSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        delayError: 2000,
        resolver: zodResolver(dobSchema),
        defaultValues: {
            dob: dateOfBirth != null ? formatWithoutTimezone(dateOfBirth) : '',
        },
    });

    const onSubmit: SubmitHandler<DobSchema> = ({ dob }) => {
        onPressNext({
            dateOfBirth: `${format(dob, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`,
        });
    };

    return (
        <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior="padding"
            keyboardVerticalOffset={120}
        >
            <YStack
                flex={1}
                justifyContent="space-between"
                gap={isNonMobileWeb ? 60 : 16}
            >
                <View>
                    <H5Emphasized marginBottom={8}>
                        What&apos;s your date of birth?
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray1000" marginBottom={24}>
                        We&apos;re required to collect this to verify your
                        identity.
                    </BodyMediumRegular>
                    <Private>
                        <Form
                            onSubmit={handleSubmit(onSubmit)}
                            marginBottom={12}
                        >
                            <ControlledDateTextInput
                                name="dob"
                                control={control}
                                register={register}
                                errors={errors}
                                onSubmitEditing={() => {
                                    Keyboard.dismiss();
                                }}
                            />
                        </Form>
                    </Private>
                </View>
                <KycButton
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </KeyboardAvoidingView>
    );
};

export default Dob;
